/**
 * @file hooks/biz/list 配置型列表生产器
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import _ from 'lodash';
import {useOriginalCopy} from '@huse/previous-value';

import {ConfigOptions, ListPageProps, ListOptions} from './interface';
import useMain from './main';
import useAction from './action';
import useToolbar from './toolbar';

const useList = (configOptions: ConfigOptions, props: ListPageProps, customFunctions: any = {}) => {
    const callbackRef = useRef<any>({});

    // 表格中事件处理
    const handleTableCommand = useCallback((type: string, payload?: any) => {
        const {options, handleAction} = callbackRef.current;
        const commandConfig = _.get(options, `commands[${type}]`);
        if (commandConfig) {
            // zhangzhe 2019-10-12 增加fromTable参数以知道是哪边发出的请求
            if (commandConfig.actionType === 'dialog' && commandConfig.dialog) {
                commandConfig.dialog.fromTable = true;
            }
            if (commandConfig.actionType === 'drawer' && commandConfig.drawer) {
                commandConfig.drawer.fromTable = true;
            }
            handleAction(commandConfig, payload);
        }
    }, []);

    // 获取当前选中的数据项方法
    const getSelectedItems = useCallback(() => {
        const {rowKey, selectedRowKeys, dataSource} = callbackRef.current;
        const selectedItems = rowKey
            ? _.map(selectedRowKeys, key => _.find(dataSource, {[rowKey]: key}))
            : _.filter(dataSource, (__, i) => _.includes(selectedRowKeys, i));
        return {selectedRowKeys, selectedItems};
    }, []);

    // 初始化配置项
    const defaultOptions: ListOptions = configOptions(props, handleTableCommand, customFunctions);
    const [options, setOptions] = useState(defaultOptions);
    const originalProps = useOriginalCopy(props);

    // 5种类型操作
    const allFunctions = useMemo(() => ({
        loadList: () => callbackRef.current.loadList(),
        loadFirstPage: () => callbackRef.current.loadFirstPage(), // 重新获取第一页数据
        checkAll: (checked: boolean) => callbackRef.current.checkAll(checked),
        ...customFunctions
    }), [customFunctions]);

    const {handleAction, dialogProps, confirmProps} = useAction(props, allFunctions, getSelectedItems);
    // 主区域操作
    const {mainListProps, tableData, setStates, mainApi}
        = useMain(options, props.actions, handleTableCommand, handleAction, customFunctions);
    const {loading, checkedAll, dataSource, selectedRowKeys, filterData} = tableData;
    const {checkAll, loadList, loadFirstPage, handleFilter, handleFilterDataChange} = mainApi;
    // 操作区和筛选区
    const {toolbarProps} = useToolbar(
        options,
        selectedRowKeys,
        checkedAll,
        loading,
        handleAction,
        handleFilter,
        handleFilterDataChange,
        customFunctions
    );

    // 重要：zhangzhe 2020-07-22 通过ref解决循环依赖问题
    callbackRef.current = {
        options,
        handleAction,
        selectedRowKeys,
        dataSource,
        rowKey: options.rowKey,
        loadList,
        loadFirstPage,
        checkAll
    };

    // 当props改变时，重新刷新整个options
    const originalTableData = useOriginalCopy(tableData);
    useEffect(() => {
        // zhangzhe 2019-10-15 记录上次筛选值，不要丢失，这块可能有坑 要格外注意
        const newOptions = configOptions(props, handleTableCommand, customFunctions);
        // 查询数据不能初始化，得用上一次的查询数据
        const filterControls = _.get(newOptions, 'filterConfig.controls');
        if (_.isArray(filterControls) && filterControls.length) {
            _.each(filterControls, filterControl => {
                const filterDataValue = filterData[filterControl.name];
                if (!_.isNil(filterDataValue)) {
                    // zhangzhe 2019-11-29 特殊逻辑：需要考虑options变化的情况，如果匹配不上上一次筛选值，需要置空
                    if (filterControl.options) {
                        const optionValues = _.map(filterControl.options, 'value');
                        // 如果是多选
                        if (_.isArray(filterDataValue)) {
                            if (_.difference(filterDataValue, optionValues).length) {
                                filterControl.value = [];
                            }
                        }
                        else if (!_.includes(optionValues, filterDataValue)) {
                            filterControl.value = '';
                        }
                    }
                    else {
                        filterControl.value = filterDataValue;
                    }
                }
            });
        }
        setOptions(newOptions);
    }, [originalProps, originalTableData]);

    // 用来渲染页面的所有属性
    const {className, title} = options;
    const listProps = {
        className,
        title,
        confirmProps,
        dialogProps,
        ...toolbarProps,
        ...mainListProps
    };
    return {
        // 设置
        options,
        setOptions,
        // 列表数据
        ...tableData,
        tableData,
        // 一系列setState方法集合
        ...setStates,
        // 常用api
        ...mainApi,
        handleAction,
        handleTableCommand,
        // 渲染属性
        listProps
    };
};

export default useList;
